import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { useNotifications, Notification } from '../../hooks/useNotifications';
import { useTranslation } from 'react-i18next';

const NotificationsDropdown: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { 
    notifications, 
    unreadCount, 
    loading, 
    pushEnabled,
    requestPushPermission, 
    markAsRead, 
    markAllAsRead 
  } = useNotifications();

  // Cerrar el dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Formatear fecha de notificación
  const formatNotificationDate = (timestamp: any): string => {
    if (!timestamp) return t('notifications.justNow');
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const locale = i18n.language === 'es' ? es : enUS;
      return formatDistanceToNow(date, { addSuffix: true, locale });
    } catch (error) {
      return t('notifications.momentAgo');
    }
  };

  // Manejar clic en notificación
  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    setIsOpen(false);
  };

  // Solicitar permiso para notificaciones push
  const handleRequestPermission = async () => {
    const success = await requestPushPermission();
    if (success) {
      alert(t('notifications.pushEnabled'));
    } else {
      alert(t('notifications.pushError'));
    }
  };

  // Renderizar contenido de notificación según tipo
  const renderNotificationContent = (notification: Notification) => {
    switch (notification.type) {
      case 'like':
        return (
          <Link 
            to={`/post/${notification.postId}`} 
            className="block"
            onClick={() => handleNotificationClick(notification)}
          >
            <span className="font-medium">{notification.fromUserName}</span> {t('notifications.likeNotification')}
          </Link>
        );
      case 'comment':
        return (
          <Link 
            to={`/post/${notification.postId}`} 
            className="block"
            onClick={() => handleNotificationClick(notification)}
          >
            <span className="font-medium">{notification.fromUserName}</span> {t('notifications.commentNotification')} 
            <span className="text-gray-600 dark:text-gray-300 italic"> {notification.content?.substring(0, 30)}{notification.content && notification.content.length > 30 ? '...' : ''}</span>
          </Link>
        );
      case 'follow':
        return (
          <Link 
            to={`/profile/${notification.fromUserId}`} 
            className="block"
            onClick={() => handleNotificationClick(notification)}
          >
            <span className="font-medium">{notification.fromUserName}</span> {t('notifications.followNotification')}
          </Link>
        );
      case 'message':
        return (
          <Link 
            to={`/messages?user=${notification.fromUserId}`} 
            className="block"
            onClick={() => handleNotificationClick(notification)}
          >
            <span className="font-medium">{notification.fromUserName}</span> {t('notifications.messageNotification')} 
            <span className="text-gray-600 dark:text-gray-300 italic"> {notification.content?.substring(0, 30)}{notification.content && notification.content.length > 30 ? '...' : ''}</span>
          </Link>
        );
      default:
        return (
          <div className="block">
            {t('notifications.newNotification')}
          </div>
        );
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Icono de notificaciones con contador */}
      <button
        className="relative p-1 rounded-full text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('notifications.title')}
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown de notificaciones */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg z-50 overflow-hidden">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="font-semibold">{t('notifications.title')}</h3>
            {unreadCount > 0 && (
              <button 
                className="text-xs text-domired-600 hover:text-domired-800 dark:hover:text-domired-400"
                onClick={markAllAsRead}
              >
                {t('notifications.markAllAsRead')}
              </button>
            )}
          </div>

          {/* Solicitar permiso para notificaciones push */}
          {!pushEnabled && (
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <button 
                className="w-full text-sm text-left text-domired-600 hover:text-domired-800 dark:hover:text-domired-400"
                onClick={handleRequestPermission}
              >
                {t('notifications.enablePush')}
              </button>
            </div>
          )}

          {/* Lista de notificaciones */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center items-center p-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-domired-600"></div>
              </div>
            ) : notifications.length > 0 ? (
              notifications.map((notification) => (
                <div 
                  key={notification.id} 
                  className={`p-3 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 ${!notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                >
                  <div className="flex items-start space-x-3">
                    <Link 
                      to={`/profile/${notification.fromUserId}`}
                      onClick={() => markAsRead(notification.id)}
                    >
                      <img 
                        src={notification.fromUserPhoto || '/default-avatar.png'} 
                        alt={notification.fromUserName}
                        className="h-10 w-10 rounded-full object-cover" 
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">
                        {renderNotificationContent(notification)}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {formatNotificationDate(notification.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                {t('notifications.empty')}
              </div>
            )}
          </div>

          {/* Enlace a todas las notificaciones */}
          <div className="p-2 border-t border-gray-200 dark:border-gray-700">
            <Link 
              to="/notifications" 
              className="block w-full text-center text-sm text-domired-600 hover:text-domired-800 dark:hover:text-domired-400 p-1"
              onClick={() => setIsOpen(false)}
            >
              {t('notifications.viewAll')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsDropdown;
